import { useEffect, useState } from "react";
import { useLanguage } from "@/lib/i18n";
import { fetchProducts, type ShopifyProduct } from "@/lib/shopify";
import { ProductCard } from "@/components/ProductCard";
import { Loader2 } from "lucide-react";

interface RelatedProductsProps {
  currentHandle: string;
}

export function RelatedProducts({ currentHandle }: RelatedProductsProps) {
  const { t } = useLanguage();
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const all = await fetchProducts(10);
        setProducts(all.filter(p => p.node.handle !== currentHandle));
      } catch (err) {
        console.error('Failed to load related products:', err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [currentHandle]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (products.length === 0) return null;

  return (
    <section className="py-8 md:py-10" aria-label="Related products">
      <div className="text-center mb-5 md:mb-7">
        <h2 className="text-2xl md:text-3xl font-bold mb-2">
          {t('product.youMayAlsoLike')}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t('product.completeRoutine')}
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.slice(0, 3).map((product) => (
          <ProductCard key={product.node.id} product={product} />
        ))}
      </div>
    </section>
  );
}
